import { create } from 'zustand';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useEffect, useRef, useCallback } from 'react';
import { useUrlValue } from './urlStateStore';
import type { SocialPlatform } from '../components/icons/SocialIcon';
import { useSearchParams } from 'react-router-dom';

// Analytics page state
interface AnalyticsState {
  dateRange: 'this-month' | 'last-month' | number | 'custom';
  customDateRange: {
    start: Date;
    end: Date;
  };
  selectedBrand: string | null;
  activeTab: string; // 'summary' or a platform ID
  
  // Actions
  setDateRange: (dateRange: 'this-month' | 'last-month' | number | 'custom') => void;
  setCustomDateRange: (range: { start: Date; end: Date }) => void;
  setSelectedBrand: (brandId: string | null) => void;
  setActiveTab: (tab: string) => void;
}

// Create the store with default values
export const useAnalyticsStore = create<AnalyticsState>((set, get) => {
  const lastMonth = subMonths(new Date(), 1);
  
  return {
    dateRange: 'last-month',
    customDateRange: {
      start: startOfMonth(lastMonth),
      end: endOfMonth(lastMonth)
    },
    selectedBrand: null,
    activeTab: 'summary',
    
    // Actions
    setDateRange: (dateRange) => set((state) => {
      if (state.dateRange === dateRange) return state;
      return { dateRange };
    }),
    setCustomDateRange: (customDateRange) => set({ customDateRange }),
    setSelectedBrand: (selectedBrand) => set((state) => {
      if (state.selectedBrand === selectedBrand) return state;
      return { selectedBrand };
    }),
    setActiveTab: (activeTab) => set((state) => {
      if (state.activeTab === activeTab) return state;
      return { activeTab };
    })
  };
});

// Hook to use analytics state with URL synchronization
export function useAnalyticsUrlState() {
  // Get the raw state and actions from Zustand
  const store = useAnalyticsStore();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Create refs to track initial values and prevent circular updates
  const isInitialMount = useRef(true);
  const updatingFromStore = useRef(false);
  const storeRef = useRef(store);
  
  // Update store ref
  useEffect(() => {
    storeRef.current = store;
  }, [store]);
  
  // Connect state values to URL parameters with stable options
  const tabOptions = useRef({ skipSync: false }).current;
  const brandOptions = useRef({ skipSync: false }).current;
  const dateRangeOptions = useRef({ skipSync: false }).current;
  
  // Connect state values to URL parameters
  const [activeTab, setActiveTab] = useUrlValue<string>(
    'platform', 
    store.activeTab,
    tabOptions
  );
  
  const [selectedBrand, setSelectedBrand] = useUrlValue<string | null>(
    'brand',
    store.selectedBrand,
    brandOptions
  );
  
  const [dateRange, setDateRange] = useUrlValue<'this-month' | 'last-month' | number | 'custom'>(
    'dateRange',
    store.dateRange,
    dateRangeOptions
  );
  
  // Clean up URL by removing old parameters
  useEffect(() => {
    // Check if we have both 'tab' and 'platform' parameters
    if (searchParams.has('tab') && searchParams.has('platform')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tab');
      
      // Also remove 'view' parameter if it exists (from Calendar page)
      if (newParams.has('view')) {
        newParams.delete('view');
      }
      
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);
  
  // On initial mount, sync the store with URL values (URL has priority)
  useEffect(() => {
    if (isInitialMount.current) {
      // Only update the store if URL values are different from defaults
      const currentStore = storeRef.current;
      
      if (activeTab !== currentStore.activeTab) currentStore.setActiveTab(activeTab);
      if (selectedBrand !== currentStore.selectedBrand) currentStore.setSelectedBrand(selectedBrand);
      if (dateRange !== currentStore.dateRange) currentStore.setDateRange(dateRange);
      
      isInitialMount.current = false;
    }
  }, []); // Empty dependency array ensures this runs only once
  
  // Create wrapper functions for setters that update both URL and store
  const setActiveTabWrapper = useCallback((newTab: string) => {
    // Skip if the value hasn't changed
    if (newTab === activeTab) return;
    
    // Update URL first
    setActiveTab(newTab);
    
    // Then update store, but mark that we're updating from a user action
    updatingFromStore.current = true;
    storeRef.current.setActiveTab(newTab);
    updatingFromStore.current = false;
  }, [activeTab, setActiveTab]);
  
  const setSelectedBrandWrapper = useCallback((newBrand: string | null) => {
    if (newBrand === selectedBrand) return;
    
    setSelectedBrand(newBrand);
    updatingFromStore.current = true;
    storeRef.current.setSelectedBrand(newBrand);
    updatingFromStore.current = false;
  }, [selectedBrand, setSelectedBrand]);
  
  const setDateRangeWrapper = useCallback((newDateRange: 'this-month' | 'last-month' | number | 'custom') => {
    if (newDateRange === dateRange) return;
    
    setDateRange(newDateRange);
    updatingFromStore.current = true;
    storeRef.current.setDateRange(newDateRange);
    updatingFromStore.current = false;
  }, [dateRange, setDateRange]);
  
  // Keep Zustand store in sync with URL changes, but only if it's coming from the URL
  // and not from our wrapper functions
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && activeTab !== storeRef.current.activeTab) {
      storeRef.current.setActiveTab(activeTab);
    }
  }, [activeTab]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && selectedBrand !== storeRef.current.selectedBrand) {
      storeRef.current.setSelectedBrand(selectedBrand);
    }
  }, [selectedBrand]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && dateRange !== storeRef.current.dateRange) {
      storeRef.current.setDateRange(dateRange);
    }
  }, [dateRange]);
  
  // Return URL-synced values and our wrapped setters
  return {
    // URL synced values
    activeTab,
    selectedBrand,
    dateRange,
    
    // URL synced setters (using our wrapper functions)
    setActiveTab: setActiveTabWrapper,
    setSelectedBrand: setSelectedBrandWrapper,
    setDateRange: setDateRangeWrapper,
    
    // Values not synced with URL
    customDateRange: store.customDateRange,
    
    // Setters not synced with URL
    setCustomDateRange: store.setCustomDateRange
  };
} 